import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewChild} from '@angular/core';
import {DetectMethodChanges, isValidNumber, isVoid, makePortfolioKey} from "../../utils";
import {HedgePositionGroup} from "../data-model/hedge-position-group";
import {HedgePositionTransaction} from "../data-model/hedge-position-transaction";
import {HedgeStateTransaction} from "../data-model/hedge-state-transaction";
import {HgTransactionPopupComponent} from "../transaction-popup/hg-transaction-popup.component";
import {filter} from "rxjs/operators";
import {MessageBusService} from "../../message-bus.service";
import {
    getGroupTotal,
    getOrderGroupTotal,
    getSubTotals
} from "../hg-totals-calculators";
import {ApgPortfolio} from "../../adjustment-pricing-grid/model/ApgPortfolio";
import {CashFlowStrategy} from "../../adjustment-control-panel/cash-flow-strategy";
import {ApgDataService} from "../../adjustment-pricing-grid/services/apg-data.service";
import {TradingInstrumentDisplayNameService} from "../../trading-instruments/trading-instrument-display-name.service";
import {HgNormalizationService} from "../services/hg-normalization.service";

@Component({
    selector: 'ets-hg-strategies-section',
    templateUrl: 'hg-strategies-section.component.html',
    styleUrls: [
        './hg-strategies-section.component.scss',
        '../hedging-grid-common-styles.scss'
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class HgStrategiesSectionComponent implements OnInit {
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _messageBus: MessageBusService,
        private readonly _apgData: ApgDataService,
        private readonly _displayNameService: TradingInstrumentDisplayNameService,
        private readonly _normalizationService: HgNormalizationService
    ) {
    }

    private _stateTransactions: HedgeStateTransaction[] = [];
    private _portfolioStrategy: CashFlowStrategy;

    selectedPortfolio: ApgPortfolio;

    esHedgingEnabled = false;

    @ViewChild(HgTransactionPopupComponent)
    transactionPopupCmp : HgTransactionPopupComponent;

    @Input() portfolioDefaultQty : number;

    getNormFactor = (asset: string): number => {
        if (!asset || !this.selectedPortfolio?.asset || asset === this.selectedPortfolio.asset) {
            return 1;
        }
        return this._normalizationService.getPointValueRatio(asset, this.selectedPortfolio.asset);
    }

    get callTransactions(): HedgePositionTransaction[] {
        return this._stateTransactions.filter(x => x.side === 'Calls')
            .flatMap(x => x.positionTransactions);
    }

    get putTransactions(): HedgePositionTransaction[] {
        return this._stateTransactions.filter(x => x.side === 'Puts')
            .flatMap(x => x.positionTransactions);
    }

    get hasCalls(): boolean {
        return this.callTransactions.length > 0;
    }

    get hasPuts(): boolean {
        return this.putTransactions.length > 0;
    }

    ngOnInit() {
        this._messageBus.of<{ enabled: boolean }>('Hg.EsHedgingModeChanged')
            .subscribe(msg => {
                this.esHedgingEnabled = msg.payload.enabled;
                this._changeDetector.detectChanges();
            });

        this._messageBus.of('Hg.RefreshQuotes')
            .pipe(
                filter(x => this._stateTransactions.length > 0)
            )
            .subscribe(x => this._changeDetector.detectChanges());


        this._messageBus.of<{portfolio: ApgPortfolio}>('Hg.HedgesCleared')
            .pipe(
                filter(x => {
                    const pfKey0 = makePortfolioKey(x.payload.portfolio);
                    const pfKey1 = makePortfolioKey(this.selectedPortfolio);
                    return pfKey0 === pfKey1;
                }),
            )
            .subscribe(x => this.onHedgesCleared());
    }

    showTransactionPopup(side: 'Calls' | 'Puts') {
        const sideTransaction =
            this._stateTransactions.find(x => x.side === side);

        const isDoubleMode = this._stateTransactions.length > 1 || this._portfolioStrategy === 'Calls & Puts';

        this.transactionPopupCmp.show(this.selectedPortfolio, this.portfolioDefaultQty, [sideTransaction], isDoubleMode);
    }

    getSubTotals(transactions: HedgePositionTransaction[], transType: 'current' | 'new') {

        const subTotal = getSubTotals(transType, transactions, this.portfolioDefaultQty, this.getNormFactor);

        return subTotal;

    }

    getGroupTotal(group: HedgePositionGroup): number {
        const groupTotal = getGroupTotal(group, this.portfolioDefaultQty, this.getNormFactor);
        return groupTotal;
    }

    getGroupTransTotal(row: HedgePositionTransaction): number {

        const transaction = this._stateTransactions
            ?.find(x => x.positionTransactions.indexOf(row) >= 0);

        if (isVoid(transaction)) {
            return undefined;
        }

        if (isVoid(transaction.orderTransactions)) {
            return undefined;
        }

        const label = row.afterState?.label || row.beforeState?.label;

        const oGroup = transaction.orderTransactions
            .find(x => x.label === label);

        const groupTransTotal = getOrderGroupTotal(oGroup, this.portfolioDefaultQty, this.getNormFactor);

        return groupTransTotal;
    }

    getGrandTotal(side: 'Calls' | 'Puts'): number {

        const total = this._stateTransactions
            ?.filter(x => x.side === side)
            .flatMap(x => x.orderTransactions)
            .map(x => getOrderGroupTotal(x, this.portfolioDefaultQty, this.getNormFactor))
            .reduce((a,b) => (a || 0) +  (b || 0), undefined);

        return total;

    }

    @DetectMethodChanges()
    setData(hedgeStateTransactions: HedgeStateTransaction[]) {

        this._stateTransactions = hedgeStateTransactions;
    }

    getRowUnderlyingDisplayName(row: HedgePositionTransaction): string {
        const positions = row.afterState?.positions || row.beforeState?.positions;
        const asset = positions?.[0]?.asset;
        if (!asset) return '';
        return this._displayNameService.getDisplayNameForTicker(asset);
    }

    getPriceClass(price: number): string {
        if (!isValidNumber(price, true)) {
            return undefined;
        }
        return price > 0 ? 'credit' : 'debit';
    }

    private onHedgesCleared() {
        this.setData([]);
    }

    @DetectMethodChanges()
    onPortfolioSelected(args: ApgPortfolio) {
        this.selectedPortfolio = args;
        this._apgData.getLastUsedTemplateOfPortfolio(args)
            .then(d => {
                this._portfolioStrategy = d?.strategy;
            })
            .catch(e => {});
        this.setData([]);
    }
}